import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Alert, Box, Button, Container, Grid, Snackbar, TextField, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined";
import DoorFrontOutlinedIcon from "@mui/icons-material/DoorFrontOutlined";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import { editorial } from "../common/editorialTheme";
import { useQuestionForm } from "../common/useQuestionForm";
import { aboutContent, ChipIcon } from "./content";
import bannerImage from "../media/about-banner.jpg";

const numerals = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X", "XI", "XII", "XIII", "XIV", "XV"];

const chipIcons: Record<ChipIcon, React.ReactElement> = {
  schedule: <ScheduleOutlinedIcon sx={{ fontSize: 16, color: editorial.secondary }} />,
  online: <DevicesOutlinedIcon sx={{ fontSize: 16, color: editorial.secondary }} />,
};

const primaryButtonSx = {
  backgroundColor: editorial.primary,
  color: "#fff",
  fontFamily: editorial.bodyFont,
  fontWeight: 500,
  textTransform: "none",
  letterSpacing: "0.01em",
  borderRadius: "4px",
  px: 3,
  py: 1.5,
  boxShadow: "0 1px 3px rgba(12, 74, 74, 0.25)",
  "&:hover": { backgroundColor: editorial.primaryContainer },
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: editorial.paper,
    fontFamily: editorial.bodyFont,
    borderRadius: "6px",
    "& fieldset": { borderColor: editorial.outlineVariant },
    "&:hover fieldset": { borderColor: editorial.secondary },
    "&.Mui-focused fieldset": { borderColor: editorial.primary },
  },
  "& .MuiInputLabel-root": { fontFamily: editorial.bodyFont },
  "& .MuiInputLabel-root.Mui-focused": { color: editorial.primary },
};

// Small uppercase label with a sage dot, used above each section heading.
const Eyebrow: React.FC<{ children: React.ReactNode; icon?: React.ReactNode }> = ({ children, icon }) => (
  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, mb: 1.5, color: editorial.secondary }}>
    {icon ?? <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: editorial.secondary }} />}
    <Typography
      component="span"
      sx={{
        fontFamily: editorial.bodyFont,
        fontSize: "0.75rem",
        fontWeight: 600,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
      }}
    >
      {children}
    </Typography>
  </Box>
);

const AboutUs: React.FC = () => {
  const { header, newHere, quote, beliefs, banner, questions } = aboutContent;
  const { formData, handleChange, handleSubmit, submitting, snackbar, handleCloseSnackbar } = useQuestionForm();

  return (
    <Box sx={{ backgroundColor: editorial.canvas, minHeight: "100vh", pb: { xs: 8, md: 12 } }}>
      <Container maxWidth="lg">
        {/* Page header */}
        <Box sx={{ pt: { xs: 5, md: 8 }, pb: { xs: 4, md: 6 } }}>
          <Eyebrow>{header.eyebrow}</Eyebrow>
          <Typography
            component="h1"
            sx={{
              fontFamily: editorial.headlineFont,
              color: editorial.primary,
              fontSize: { xs: "2.5rem", md: "3.5rem" },
              lineHeight: 1.12,
              letterSpacing: "-0.015em",
            }}
          >
            {header.title}
          </Typography>
        </Box>

        {/* New Here? banner */}
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            backgroundColor: editorial.surfaceContainerLow,
            border: `1px solid ${editorial.surfaceContainerHigh}`,
            borderRadius: "16px",
            boxShadow: editorial.cardShadow,
            p: { xs: 3, sm: 4, lg: 6 },
            mb: { xs: 8, md: 10 },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              right: -64,
              top: -64,
              width: 320,
              height: 320,
              borderRadius: "50%",
              backgroundColor: `${editorial.secondaryContainer}4D`,
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              left: -48,
              bottom: -48,
              width: 192,
              height: 192,
              borderRadius: "50%",
              backgroundColor: `${editorial.primaryFixed}33`,
              pointerEvents: "none",
            }}
          />
          <Grid container spacing={4} alignItems="center" sx={{ position: "relative" }}>
            <Grid item xs={12} lg={8}>
              <Eyebrow icon={<DoorFrontOutlinedIcon sx={{ fontSize: 20 }} />}>{newHere.eyebrow}</Eyebrow>
              <Typography
                component="h2"
                sx={{
                  fontFamily: editorial.headlineFont,
                  color: editorial.primary,
                  fontSize: { xs: "1.5rem", lg: "1.875rem" },
                  lineHeight: 1.25,
                  mb: 1.5,
                }}
              >
                {newHere.heading}
              </Typography>
              <Typography
                sx={{
                  fontFamily: editorial.bodyFont,
                  color: editorial.textVariant,
                  fontSize: "1rem",
                  lineHeight: 1.7,
                  maxWidth: 672,
                  mb: 3,
                }}
              >
                {newHere.body}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                {newHere.chips.map((chip) => (
                  <Box
                    key={chip.label}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1,
                      backgroundColor: editorial.paper,
                      border: `1px solid ${editorial.surfaceContainerHigh}`,
                      borderRadius: "6px",
                      px: 1.75,
                      py: 1,
                      boxShadow: editorial.cardShadow,
                    }}
                  >
                    {chipIcons[chip.icon]}
                    <Typography
                      component="span"
                      sx={{ fontFamily: editorial.bodyFont, fontSize: "0.8rem", fontWeight: 500, color: editorial.primary }}
                    >
                      {chip.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} lg={4} sx={{ display: "flex", justifyContent: { xs: "flex-start", lg: "center" } }}>
              <Button
                component={RouterLink}
                to={newHere.cta.to}
                endIcon={<ArrowForwardIcon />}
                sx={{ ...primaryButtonSx, width: { xs: "100%", sm: "auto", lg: "100%" } }}
              >
                {newHere.cta.label}
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Pull quote */}
        <Box sx={{ maxWidth: 896, mx: "auto", mb: { xs: 8, md: 12 }, textAlign: "center" }}>
          <Box
            sx={{
              py: 4,
              borderTop: `1px solid ${editorial.surfaceContainerHigh}`,
              borderBottom: `1px solid ${editorial.surfaceContainerHigh}`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <FormatQuoteIcon sx={{ fontSize: 40, color: editorial.secondary, opacity: 0.8, mb: 2 }} />
            <Typography
              component="blockquote"
              sx={{
                fontFamily: editorial.headlineFont,
                fontStyle: "italic",
                color: editorial.primary,
                fontSize: { xs: "1.5rem", md: "1.875rem", lg: "2.125rem" },
                lineHeight: 1.35,
                m: 0,
                mb: 2,
              }}
            >
              “{quote.text}”
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ width: 24, height: "1px", backgroundColor: `${editorial.secondary}80` }} />
              <Typography
                sx={{
                  fontFamily: editorial.bodyFont,
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: editorial.secondary,
                }}
              >
                {quote.attribution}
              </Typography>
              <Box sx={{ width: 24, height: "1px", backgroundColor: `${editorial.secondary}80` }} />
            </Box>
          </Box>
        </Box>

        {/* Statement of Beliefs -- the church's text verbatim, one numbered entry per "We believe" paragraph */}
        <Box sx={{ maxWidth: 760, mx: "auto", mb: { xs: 8, md: 10 } }}>
          <Eyebrow>{beliefs.eyebrow}</Eyebrow>
          <Typography
            component="h2"
            sx={{
              fontFamily: editorial.headlineFont,
              color: editorial.primary,
              fontSize: { xs: "1.875rem", sm: "2.25rem", lg: "2.75rem" },
              letterSpacing: "-0.015em",
              lineHeight: 1.15,
              mb: 3,
            }}
          >
            {beliefs.title}
          </Typography>
          <Typography
            sx={{
              fontFamily: editorial.bodyFont,
              fontStyle: "italic",
              color: editorial.textVariant,
              fontSize: "1.125rem",
              lineHeight: 1.75,
              mb: 2,
            }}
          >
            {beliefs.intro[0]}
          </Typography>
          {beliefs.intro.slice(1).map((paragraph) => (
            <Typography
              key={paragraph}
              sx={{ fontFamily: editorial.bodyFont, color: editorial.textVariant, fontSize: "1rem", lineHeight: 1.75, mb: 2 }}
            >
              {paragraph}
            </Typography>
          ))}

          <Box
            sx={{
              mt: 5,
              px: { xs: 3, md: 5 },
              py: { xs: 1, md: 2 },
              backgroundColor: editorial.paper,
              border: `1px solid ${editorial.surfaceContainerHigh}`,
              borderRadius: "12px",
              boxShadow: editorial.cardShadow,
            }}
          >
            {beliefs.entries.map((paragraphs, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: { xs: 1.5, sm: 3 },
                  py: { xs: 3, md: 3.5 },
                  borderBottom: index < beliefs.entries.length - 1 ? `1px solid ${editorial.divider}` : "none",
                }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    backgroundColor: editorial.surfaceContainerHigh,
                    color: editorial.primary,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: editorial.headlineFont,
                    fontSize: "0.875rem",
                    fontWeight: 600,
                  }}
                >
                  {numerals[index]}
                </Box>
                <Box>
                  {paragraphs.map((paragraph, pIndex) => (
                    <Typography
                      key={pIndex}
                      sx={{
                        fontFamily: editorial.bodyFont,
                        color: editorial.textVariant,
                        fontSize: "1rem",
                        lineHeight: 1.75,
                        mt: pIndex > 0 ? 2 : 0,
                      }}
                    >
                      {paragraph}
                    </Typography>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>

          {/* Summary */}
          <Box
            sx={{
              mt: 4,
              p: { xs: 3, md: 5 },
              backgroundColor: `${editorial.secondaryContainer}59`,
              border: `1px solid ${editorial.secondaryContainer}`,
              borderRadius: "12px",
            }}
          >
            <Typography
              component="h3"
              sx={{
                fontFamily: editorial.headlineFont,
                color: editorial.primary,
                fontSize: { xs: "1.25rem", md: "1.5rem" },
                mb: 2.5,
              }}
            >
              {beliefs.summary.heading}
            </Typography>
            <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, display: "grid", gap: 1.5 }}>
              {beliefs.summary.items.map((item) => (
                <Box component="li" key={item} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 20, color: editorial.secondary, mt: "3px", flexShrink: 0 }} />
                  <Typography sx={{ fontFamily: editorial.bodyFont, color: editorial.text, fontSize: "1rem", lineHeight: 1.6 }}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Photo banner */}
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            borderRadius: "16px",
            boxShadow: editorial.cardShadow,
            backgroundColor: editorial.surfaceContainerHigh,
            height: { xs: 280, md: 352 },
            mb: { xs: 8, md: 12 },
            "&:hover img": { transform: "scale(1.02)" },
          }}
        >
          <Box
            component="img"
            src={bannerImage}
            alt=""
            loading="lazy"
            sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transition: "transform 0.7s" }}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              p: { xs: 3, sm: 4, lg: 7 },
              background: {
                xs: "linear-gradient(to right, rgba(12, 74, 74, 0.88), rgba(12, 74, 74, 0.6))",
                md: "linear-gradient(to right, rgba(12, 74, 74, 0.8), rgba(12, 74, 74, 0.4), transparent)",
              },
            }}
          >
            <Box sx={{ maxWidth: 512, color: "#fff" }}>
              <Typography
                sx={{
                  fontFamily: editorial.bodyFont,
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: editorial.primaryFixed,
                  mb: 1,
                }}
              >
                {banner.eyebrow}
              </Typography>
              <Typography
                sx={{
                  fontFamily: editorial.headlineFont,
                  fontWeight: 300,
                  fontSize: { xs: "1.5rem", lg: "1.875rem" },
                  lineHeight: 1.35,
                }}
              >
                {banner.text}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Questions -- same form and endpoint as the Visitors page */}
        <Box
          sx={{
            maxWidth: 1024,
            mx: "auto",
            backgroundColor: `${editorial.surfaceContainerHigh}B3`,
            border: `1px solid ${editorial.surfaceContainerHigh}`,
            borderRadius: "16px",
            p: { xs: 3, sm: 5, lg: 7 },
          }}
        >
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                backgroundColor: editorial.secondaryContainer,
                color: editorial.secondary,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2.5,
                boxShadow: editorial.cardShadow,
              }}
            >
              <ForumOutlinedIcon sx={{ fontSize: 28 }} />
            </Box>
            <Typography
              component="h2"
              sx={{
                fontFamily: editorial.headlineFont,
                color: editorial.primary,
                fontSize: { xs: "1.5rem", lg: "1.875rem" },
                mb: 1.5,
              }}
            >
              {questions.heading}
            </Typography>
            <Typography
              sx={{
                fontFamily: editorial.bodyFont,
                color: editorial.textVariant,
                fontSize: { xs: "1rem", lg: "1.125rem" },
                fontWeight: 300,
                lineHeight: 1.6,
                maxWidth: 576,
                mx: "auto",
              }}
            >
              {questions.body}
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 640, mx: "auto" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField required fullWidth label="Name" name="name" value={formData.name} onChange={handleChange} sx={fieldSx} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Phone" name="phone" value={formData.phone} onChange={handleChange} sx={fieldSx} />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  multiline
                  rows={4}
                  label="Message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <Button type="submit" fullWidth disabled={submitting} sx={primaryButtonSx}>
                  {submitting ? "Sending..." : "Send Message"}
                </Button>
              </Grid>
            </Grid>
            <Typography
              sx={{ fontFamily: editorial.bodyFont, color: editorial.textVariant, fontSize: "0.875rem", textAlign: "center", mt: 2.5 }}
            >
              Or call us at{" "}
              <Box
                component="a"
                href={`tel:${questions.phone.replace(/\D/g, "")}`}
                sx={{ color: editorial.primary, fontWeight: 500, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
              >
                {questions.phone}
              </Box>
            </Typography>
          </Box>
        </Box>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AboutUs;
